import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LifeBuoy, Loader2, MessageSquare, Paperclip, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatusPill } from '@/components/panels/SectionCard';
import { getErrorMessage } from '@/lib/errors';
import {
  supportApi,
  supportKeys,
  statusTone,
  type TicketPriority,
} from '@/lib/support-api';
import { relativeTime } from '@/modules/monitoring/format';

/**
 * The customer's own tickets.
 *
 * ## Opening a ticket asks for a subject *and* a description
 *
 * Both required. A queue of subject-only tickets means the first reply is always "what happened?",
 * which costs a day. One extra text area up front is the cheaper trade for everybody.
 *
 * ## Nothing here is subscription-gated
 *
 * Deliberate, and enforced on the server rather than only here: a customer whose subscription has
 * lapsed is exactly the customer who needs support, frequently *about* the lapse. Gating this
 * would make the billing problem unreportable through the product that caused it.
 */
export function SupportPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [composing, setComposing] = useState(false);

  const tickets = useQuery({
    queryKey: supportKeys.tickets(page),
    queryFn: () => supportApi.list(page),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">Support</h1>
          <p className="text-sm text-muted-foreground">
            Ask us anything about the platform. Replies arrive here.
          </p>
        </div>
        {!composing && (
          <Button className="ml-auto" onClick={() => setComposing(true)}>
            <Plus className="mr-2 size-4" />
            New ticket
          </Button>
        )}
      </div>

      {composing && (
        <NewTicketForm
          onCancel={() => setComposing(false)}
          onOpened={() => {
            setComposing(false);
            setPage(1);
            void queryClient.invalidateQueries({ queryKey: ['support', 'tickets'] });
          }}
        />
      )}

      {tickets.isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Loading your tickets…
        </div>
      )}

      {tickets.isError && <Alert variant="danger">{getErrorMessage(tickets.error)}</Alert>}

      {tickets.data !== undefined && tickets.data.items.length === 0 && !composing && (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <span className="inline-flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <LifeBuoy className="size-6" />
          </span>
          <p className="font-medium">No tickets yet</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            If something is not working or you are not sure how a part of the platform is meant to
            behave, open a ticket and we will pick it up.
          </p>
          <Button className="mt-2" onClick={() => setComposing(true)}>
            <Plus className="mr-2 size-4" />
            Open your first ticket
          </Button>
        </Card>
      )}

      {tickets.data !== undefined && tickets.data.items.length > 0 && (
        <div className="space-y-3">
          {tickets.data.items.map((ticket) => (
            <Link key={ticket.id} to={`/support/${ticket.id}`} className="block">
              <Card className="p-4 transition-colors hover:border-primary/40">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs text-muted-foreground">
                        {ticket.reference}
                      </span>
                      <StatusPill label={ticket.status_label} tone={statusTone(ticket.status)} />
                    </div>
                    <p className="mt-1 truncate font-medium">{ticket.subject}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {ticket.last_message_from === 'staff'
                        ? `Support replied ${relativeTime(ticket.last_message_at)}`
                        : `You wrote ${relativeTime(ticket.last_message_at)}`}
                      {/* Said plainly rather than left to be inferred from the status. "Nobody
                          has replied yet" is the thing a waiting customer wants confirmed. */}
                      {!ticket.has_been_answered && ' · not answered yet'}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <MessageSquare className="size-3.5" />
                      {ticket.message_count}
                    </span>
                    {ticket.attachment_count > 0 && (
                      <span className="flex items-center gap-1">
                        <Paperclip className="size-3.5" />
                        {ticket.attachment_count}
                      </span>
                    )}
                  </div>
                </div>
              </Card>
            </Link>
          ))}

          {tickets.data.total > tickets.data.per_page && (
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {Math.ceil(tickets.data.total / tickets.data.per_page)}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page * tickets.data.per_page >= tickets.data.total}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NewTicketForm({
  onCancel,
  onOpened,
}: {
  onCancel: () => void;
  onOpened: () => void;
}) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('normal');

  const open = useMutation({
    mutationFn: () => supportApi.open(subject, body, priority),
    onSuccess: onOpened,
  });

  return (
    <Card className="space-y-4 p-6">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">New ticket</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Tell us what you were doing and what happened. If it involves a server or a run, naming it
          saves a round trip.
        </p>
      </div>

      {open.isError && <Alert variant="danger">{getErrorMessage(open.error)}</Alert>}

      <div className="space-y-2">
        <Label htmlFor="ticket-subject">Subject</Label>
        <Input
          id="ticket-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={150}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="ticket-body">What happened</Label>
        <textarea
          id="ticket-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={7}
          className="w-full resize-y rounded-md border bg-background p-3 text-sm outline-none focus:border-primary/50"
          aria-describedby="ticket-body-hint"
        />
        <p id="ticket-body-hint" className="text-xs text-muted-foreground">
          Include the step it stopped on, what the log said and what you expected instead.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="ticket-priority">Priority</Label>
        <select
          id="ticket-priority"
          value={priority}
          onChange={(e) => setPriority(e.target.value as TicketPriority)}
          className="w-full rounded-md border bg-background p-2 text-sm outline-none focus:border-primary/50"
        >
          <option value="low">Low. A question, nothing is broken</option>
          <option value="normal">Normal</option>
          <option value="high">High. Something is not working</option>
        </select>
        {/* `urgent` is absent on purpose and refused by the API. A priority every reporter can max
            out stops ordering anything for the reporters who did not. */}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => open.mutate()}
          disabled={open.isPending || subject.trim() === '' || body.trim() === ''}
        >
          {open.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
          Open ticket
        </Button>
        <Button variant="outline" onClick={onCancel} disabled={open.isPending}>
          Cancel
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        You can attach files once the ticket is open.
      </p>
    </Card>
  );
}
